import React from 'react';
import { Form } from '@ant-design/compatible';
import { BRAND_PB_NB_TYPE } from '../../../../constants/service/app/brandConstants';
import ScrollNavigator from '../../common/ScrollNavigator';
import ProductBottomBar from '../../common/form/ProductBottomBar';
import ProductStatusAndBrandInfo from '../../common/form/ProductStatusAndBrandInfo';
import ProductCategory from '../../common/form/ProductCategory';
import ProductProperty from '../../common/form/ProductProperty';
import ProductNameAndPrice from '../../common/form/ProductNameAndPrice';
import ProductStockAndOption from '../../common/form/ProductStockAndOption';
import ProductPayMethod from '../../common/form/ProductPayMethod';
import ProductTagInfo from '../../common/form/ProductTagInfo';
import ProductNoticeInfo from '../../common/form/ProductNoticeInfo';
import ProductImg from '../../common/form/ProductImg';
import ProductDescriptionImg from '../../common/form/ProductDescriptionImg';
import ProductDeliveryInfo from '../../common/form/ProductDeliveryInfo';
import ProductAfterService from '../../common/form/ProductAfterService';
import ProductCustomAttribute from '../../common/form/ProductCustomAttribute';
import useSubmit from '../../../../hooks/product/useSubmit';
import {
  PRODUCT_DETAIL_PAGE_TYPE,
  PRODUCT_MANAGEMENT_TYPE,
  PRODUCT_STATUS,
} from '../../../../constants/service/app/productConstants';
import './ProductDetailWrap.scss';

const ProductDetailWrap = ({ pageType, form, data }) => {
  const {
    productDetail,
    storeSelectList,
    mainProductCategorySelectList,
    subProductCategorySelectList,
    productPayMethodTypeInfo,
    productPropertyList,
    productTagInfo,
    customFieldDefinitions,
  } = data;
  const {
    productId,
    proInfo,
    proStatusAndBrandInfo,
    proNameAndPrice,
    proCategory,
    proStockAndOption,
    proNoticeProduct,
    proImg,
    proDescriptionImg,
    proDeliveryInfo,
    proAfterService,
    productExternalInfo,
  } = productDetail;

  const customAttributes = customFieldDefinitions || [];
  const customAttributeKeys = customAttributes.map((attr) => attr.key);

  const {
    onInsert,
    onEdit,
    deleteProduct,
    permitProduct,
    permitBanProduct,
    rejectProduct,
    getRejectReasonList,
    openPreview,
  } = useSubmit(form, productDetail, customAttributeKeys);

  const isEditable = pageType !== PRODUCT_DETAIL_PAGE_TYPE.DETAIL;

  /**
   * 사방넷 전송 팝업 노출 조건
   * 판매중, 품절 상태일때만 노출
   * 단 승인대기시에 승인하기할때는 노출되어야함
   * PB : 입점 또는 옵션이 있는 경우 입점 옵션이 있을것
   * NB : 입고 또는 옵션이 있는 경우 입고 옵션이 있을것
   */
  const shouldConfirmToSendExternalChannel = () => {
    if (
      proStatusAndBrandInfo?.status !== PRODUCT_STATUS.CONFIRM_READY &&
      proStatusAndBrandInfo?.status !== PRODUCT_STATUS.NOW_ON_SALE &&
      proStatusAndBrandInfo?.status !== PRODUCT_STATUS.SOLD_OUT
    ) {
      return false;
    }

    if (
      proStatusAndBrandInfo?.status === PRODUCT_STATUS.CONFIRM_READY &&
      pageType !== PRODUCT_DETAIL_PAGE_TYPE.DETAIL
    ) {
      return false;
    }

    let currentOptionList = [];
    let currentProductManagementType;

    if (pageType === PRODUCT_DETAIL_PAGE_TYPE.EDIT) {
      // 편집시에는 편집중인 현재 상태의 값을 가져와야함
      if (form.getFieldsValue().optionList && form.getFieldsValue().optionList.length > 0) {
        currentOptionList = form.getFieldsValue().optionList;
      } else {
        currentProductManagementType = form.getFieldsValue().feeInfo?.productManagementType;
      }
    } else {
      if (proStockAndOption?.optionList && proStockAndOption.optionList.length > 0) {
        currentOptionList = proStockAndOption.optionList;
      } else {
        currentProductManagementType = proStockAndOption?.feeInfo?.productManagementType;
      }
    }

    // 옵션이 있을때
    if (currentOptionList && currentOptionList.length > 0) {
      const isPbEnter =
        proStatusAndBrandInfo?.brandClassification === BRAND_PB_NB_TYPE.PB &&
        currentOptionList?.some((option) => option?.productManagementType === PRODUCT_MANAGEMENT_TYPE.ENTER);

      const isNbIncome =
        proStatusAndBrandInfo?.brandClassification === BRAND_PB_NB_TYPE.NB &&
        currentOptionList?.some((option) => option?.productManagementType === PRODUCT_MANAGEMENT_TYPE.INCOME);

      return isPbEnter || isNbIncome;
    }

    // 옵션이 없을때
    const isPbEnter =
      proStatusAndBrandInfo?.brandClassification === BRAND_PB_NB_TYPE.PB &&
      currentProductManagementType === PRODUCT_MANAGEMENT_TYPE.ENTER;

    const isNbIncome =
      proStatusAndBrandInfo?.brandClassification === BRAND_PB_NB_TYPE.NB &&
      currentProductManagementType === PRODUCT_MANAGEMENT_TYPE.INCOME;

    return isPbEnter || isNbIncome;
  };

  const onSubmit = (param) => {
    if (pageType === PRODUCT_DETAIL_PAGE_TYPE.CREATE || pageType === PRODUCT_DETAIL_PAGE_TYPE.COPY) {
      // 상품 등록 및 복사
      onInsert();
    } else if (pageType === PRODUCT_DETAIL_PAGE_TYPE.EDIT) {
      onEdit(param);
    }
  };

  return (
    <div className="product-detail-wrap">
      {/* 하단 네브바 */}
      <ProductBottomBar
        form={form}
        pageType={pageType}
        proStatusAndBrandInfo={proStatusAndBrandInfo}
        productId={productId}
        productExternalInfo={productExternalInfo}
        openPreview={openPreview}
        getRejectReasonList={getRejectReasonList}
        permitProduct={permitProduct}
        permitBanProduct={permitBanProduct}
        rejectProduct={rejectProduct}
        deleteProduct={deleteProduct}
        onSubmit={onSubmit}
        shouldConfirmToSendExternalChannel={shouldConfirmToSendExternalChannel}
      />

      <ScrollNavigator
        marginTop={24}
        list={[
          {
            title: '상품 상태 및 브랜드 정보',
            content: (
              <ProductStatusAndBrandInfo
                pageType={pageType}
                form={form}
                initialValue={proStatusAndBrandInfo}
                storeSelectList={storeSelectList}
              />
            ),
          },
          {
            title: '카테고리',
            content: (
              <ProductCategory
                form={form}
                isEditable={isEditable}
                initialValue={proCategory}
                mainProductCategorySelectList={mainProductCategorySelectList}
                subProductCategorySelectList={subProductCategorySelectList}
              />
            ),
          },
          {
            title: '상품 판매/옵션 정보',
            content: (
              <>
                <ProductNameAndPrice
                  form={form}
                  isEditable={isEditable}
                  initialValue={proNameAndPrice}
                  minimumPrice={proCategory.minimumPrice}
                />
                <ProductStockAndOption
                  pageType={pageType}
                  form={form}
                  isEditable={isEditable}
                  initialValue={proStockAndOption}
                  minimumPrice={proCategory.minimumPrice}
                  proInfo={proInfo}
                />
                <ProductPayMethod
                  isEditable={isEditable}
                  form={form}
                  initialValue={proInfo}
                  productPayMethodTypeInfo={productPayMethodTypeInfo}
                />
              </>
            ),
          },
          {
            title: '속성 및 태그정보',
            content: (
              <>
                <ProductProperty
                  form={form}
                  isEditable={isEditable}
                  productId={productDetail.productId}
                  initialValue={proInfo}
                  propertyList={productPropertyList}
                />
                <ProductTagInfo
                  isEditable={isEditable}
                  form={form}
                  initialValue={proInfo.productTagInfo}
                  productTagInfo={productTagInfo}
                />
              </>
            ),
          },
          {
            title: [PRODUCT_DETAIL_PAGE_TYPE.DETAIL, PRODUCT_DETAIL_PAGE_TYPE.EDIT].includes(pageType)
              ? '상품별 공지사항 관리'
              : '',
            content: (
              <>
                {[PRODUCT_DETAIL_PAGE_TYPE.DETAIL, PRODUCT_DETAIL_PAGE_TYPE.EDIT].includes(pageType) ? (
                  <ProductNoticeInfo initialValue={proNoticeProduct} />
                ) : null}
              </>
            ),
          },
          {
            title: '상품 이미지/상세 설명',
            content: (
              <>
                <ProductImg isEditable={isEditable} form={form} initialValue={proImg} />
                <ProductDescriptionImg isEditable={isEditable} form={form} initialValue={proDescriptionImg} />
              </>
            ),
          },
          {
            title: '배송 및 A/S 정보',
            content: (
              <>
                <ProductDeliveryInfo isEditable={isEditable} form={form} initialValue={proDeliveryInfo} />
                <ProductAfterService isEditable={isEditable} form={form} initialValue={proAfterService} />
              </>
            ),
          },
          // 커스텀 속성은 상세(읽기)와 편집만 가능 (등록/복사 불가)
          ...([PRODUCT_DETAIL_PAGE_TYPE.DETAIL, PRODUCT_DETAIL_PAGE_TYPE.EDIT].includes(pageType)
            ? [
                {
                  title: '커스텀 속성',
                  content: (
                    <ProductCustomAttribute form={form} isEditable={isEditable} initialValue={customAttributes} />
                  ),
                },
              ]
            : []),
        ]}
      />
    </div>
  );
};

export default Form.create()(ProductDetailWrap);
